/**
 * Enhanced Kanban Board Hook - Mouse Wheel Zoom & Smooth Scroll
 * Provides Jira-style zoom controls with mouse wheel and keyboard
 * 
 * NOTE: Display labels are offset by +30% for better UX:
 * - Actual 0.7 scale displays as "100%" (default)
 * - Actual 0.5 scale displays as "70%" (minimum)  
 * - Actual 1.1 scale displays as "140%" (maximum)
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

export type ZoomScale = number; // 0.5 to 1.1

// Display offset - actual zoom is 30% less than displayed
const DISPLAY_OFFSET = 0.3;

interface UseKanbanZoomOptions {
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
  enableMouseWheel?: boolean;
  enableKeyboard?: boolean;
}

export function useKanbanZoom(options: UseKanbanZoomOptions = {}) {
  const {
    minZoom = 0.5,   // Displays as 80%
    maxZoom = 1.1,   // Displays as 140%
    zoomStep = 0.1,
    enableMouseWheel = true,
    enableKeyboard = true
  } = options;

  // Default zoom is 0.7 (displays as 100%) - makes cards 30% smaller by default
  const [zoomScale, setZoomScale] = useState<number>(0.7);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isPinching, setIsPinching] = useState(false);

  // Clamp zoom value within bounds
  const clampZoom = useCallback((value: number) => {
    return Math.max(minZoom, Math.min(maxZoom, value));
  }, [minZoom, maxZoom]);

  // Set zoom with clamping
  const setZoom = useCallback((value: number | ((prev: number) => number)) => {
    setZoomScale(prev => {
      const newValue = typeof value === 'function' ? value(prev) : value;
      return clampZoom(newValue);
    });
  }, [clampZoom]);

  // Zoom in
  const zoomIn = useCallback(() => {
    setZoom(prev => prev + zoomStep);
  }, [setZoom, zoomStep]);

  // Zoom out
  const zoomOut = useCallback(() => {
    setZoom(prev => prev - zoomStep);
  }, [setZoom, zoomStep]);

  // Reset zoom to default (0.7 actual = 100% displayed)
  const resetZoom = useCallback(() => {
    setZoom(0.7);
  }, [setZoom]);

  // Mouse wheel zoom (Ctrl/Cmd + Wheel)
  useEffect(() => {
    if (!enableMouseWheel) return;

    const handleWheel = (e: WheelEvent) => {
      // Only zoom when Ctrl or Cmd is pressed
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        
        // Normalize wheel delta (different browsers have different values)
        const delta = Math.sign(e.deltaY) * -1;
        const zoomChange = delta * zoomStep;
        
        setZoom(prev => prev + zoomChange);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [enableMouseWheel, setZoom, zoomStep]);

  // Keyboard shortcuts (Ctrl/Cmd + Plus/Minus/0)
  useEffect(() => {
    if (!enableKeyboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          zoomIn();
        } else if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          zoomOut();
        } else if (e.key === '0') {
          e.preventDefault();
          resetZoom();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableKeyboard, zoomIn, zoomOut, resetZoom]);

  // Touch pinch zoom (for tablets)
  useEffect(() => {
    if (!containerRef.current) return;

    let initialDistance = 0;
    let initialZoom = 1;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        setIsPinching(true);
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        initialDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        initialZoom = zoomScale;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && isPinching) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        
        const scale = currentDistance / initialDistance;
        setZoom(initialZoom * scale);
      }
    };

    const handleTouchEnd = () => {
      setIsPinching(false);
    };

    const container = containerRef.current;
    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPinching, setZoom, zoomScale]);

  // Calculate zoom level label (offset by +30% for display)
  // Actual 0.7 shows as "100%", actual 0.5 shows as "80%", actual 1.1 shows as "140%"
  const zoomLabel = `${Math.round((zoomScale + DISPLAY_OFFSET) * 100)}%`;
  const canZoomIn = zoomScale < maxZoom;
  const canZoomOut = zoomScale > minZoom;

  return {
    zoomScale,
    zoomLabel,
    setZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    containerRef,
    isPinching
  };
}
